import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, ChevronDown } from "lucide-react";

/**
 * LiveChatStoryCard — client-report chapter card showing Live Chat
 * performance for the selected period. Diplomatic tone: growth is
 * celebrated in lime, dips are framed as "worth a conversation" in amber.
 * Never red, never alarming.
 *
 * Data shape (from GET /api/live-chat/:client_id):
 *   records: [{
 *     client_id, report_start, report_end, location_name,
 *     total_chats, total_leads, billable_leads, chat_to_lead_pct,
 *     lead_types: [{ type, count }],
 *   }]
 */

const fmt = (n) => {
  if (n == null || isNaN(n)) return "0";
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 10_000) return (n / 1_000).toFixed(1) + "K";
  if (n >= 1_000) return n.toLocaleString();
  return String(Math.round(n));
};

const fmtPeriodLabel = (start, end) => {
  if (!start || !end) return "-";
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  try {
    const s = start.split("-");
    const e = end.split("-");
    const sM = months[parseInt(s[1], 10) - 1];
    const eM = months[parseInt(e[1], 10) - 1];
    if (s[0] === e[0] && s[1] === e[1]) return `${sM} ${s[0]}`;
    return `${sM} ${s[0]} – ${eM} ${e[0]}`;
  } catch { return `${start} – ${end}`; }
};

const computeDelta = (current, prior, minSample = 3) => {
  if (current == null || prior == null) return null;
  if (prior < minSample && current < minSample) return null;
  if (prior === 0) return current > 0 ? { pct: null, direction: "up", isNew: true } : null;
  const pct = ((current - prior) / prior) * 100;
  if (Math.abs(pct) < 0.5) return { pct: 0, direction: "flat" };
  return { pct, direction: pct > 0 ? "up" : "down" };
};

const DeltaPill = ({ delta }) => {
  if (!delta) return null;
  if (delta.isNew) {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-bold tabular-nums bg-lime-400/15 border border-lime-400/30 text-lime-300">
        NEW
      </span>
    );
  }
  if (delta.direction === "flat") {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-semibold tabular-nums bg-slate-700/40 border border-slate-600/40 text-slate-400">
        flat
      </span>
    );
  }
  const up = delta.direction === "up";
  const cls = up
    ? "bg-lime-400/15 border-lime-400/30 text-lime-300"
    : "bg-amber-400/10 border-amber-400/25 text-amber-300";
  const arrow = up ? "▲" : "▼";
  const display = Math.abs(delta.pct) >= 100
    ? `${up ? "+" : "−"}${Math.round(Math.abs(delta.pct))}%`
    : `${up ? "+" : "−"}${Math.abs(delta.pct).toFixed(Math.abs(delta.pct) >= 10 ? 0 : 1)}%`;
  return (
    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[10px] font-bold tabular-nums border ${cls}`}>
      <span className="text-[8px]">{arrow}</span>{display}
    </span>
  );
};

// Colour palette for lead-type bars. Cycles if there are more types than colours.
const LEAD_COLOURS = [
  { bar: "bg-cyan-400", chip: "bg-cyan-500/15 text-cyan-200 border-cyan-400/30" },
  { bar: "bg-emerald-400", chip: "bg-emerald-500/15 text-emerald-200 border-emerald-400/30" },
  { bar: "bg-indigo-400", chip: "bg-indigo-500/15 text-indigo-200 border-indigo-400/30" },
  { bar: "bg-amber-400", chip: "bg-amber-500/15 text-amber-200 border-amber-400/30" },
  { bar: "bg-fuchsia-400", chip: "bg-fuchsia-500/15 text-fuchsia-200 border-fuchsia-400/30" },
];

const KPI = ({ label, value, delta, testId }) => (
  <div className="bg-slate-900/50 border border-cyan-400/15 rounded-xl p-3.5" data-testid={testId}>
    <p className="text-[10px] uppercase tracking-[0.16em] text-slate-400 font-bold mb-1">{label}</p>
    <div className="flex items-baseline gap-2">
      <p className="text-white text-2xl md:text-3xl font-bold tabular-nums">{value}</p>
      <DeltaPill delta={delta} />
    </div>
  </div>
);

const LeadTypeRow = ({ label, count, total, colour, prior }) => {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  const delta = computeDelta(count, prior);
  return (
    <div className="mb-2 last:mb-0" data-testid={`lc-lead-type-${label.toLowerCase()}`}>
      <div className="flex items-center justify-between text-[11px] mb-1">
        <span className="inline-flex items-center gap-2">
          <span className="text-slate-200 font-semibold">{label}</span>
          <DeltaPill delta={delta} />
        </span>
        <span className="text-slate-400 tabular-nums">
          <span className="text-white font-semibold">{count}</span> · {pct}%
        </span>
      </div>
      <div className="h-2 rounded-full overflow-hidden bg-slate-800/70">
        <div className={`h-full ${colour}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

export default function LiveChatStoryCard({ records = [], clientName = "Your business" }) {
  const [isOpen, setIsOpen] = useState(false);

  // Group by (report_start, report_end) — one Live Chat record per period.
  const periods = useMemo(() => {
    if (!records || records.length === 0) return [];
    const map = new Map();
    for (const r of records) {
      const key = `${r.report_start}__${r.report_end}`;
      if (!map.has(key)) {
        map.set(key, { key, start: r.report_start, end: r.report_end, record: r });
      }
    }
    return Array.from(map.values()).sort((a, b) => (b.end || "").localeCompare(a.end || ""));
  }, [records]);

  const [selectedKey, setSelectedKey] = useState(null);
  const selectedPeriod = useMemo(() => {
    if (periods.length === 0) return null;
    if (!selectedKey) return periods[0];
    return periods.find((p) => p.key === selectedKey) || periods[0];
  }, [periods, selectedKey]);

  const priorPeriod = useMemo(() => {
    if (!selectedPeriod) return null;
    const i = periods.findIndex((p) => p.key === selectedPeriod.key);
    return i >= 0 && i < periods.length - 1 ? periods[i + 1] : null;
  }, [periods, selectedPeriod]);

  if (!records || records.length === 0 || !selectedPeriod) return null;

  const rec = selectedPeriod.record;
  const prior = priorPeriod?.record || null;

  const totalLeadTypeCount = (rec.lead_types || []).reduce((s, lt) => s + (lt.count || 0), 0);
  const sortedLeadTypes = [...(rec.lead_types || [])].sort((a, b) => (b.count || 0) - (a.count || 0));
  const priorLeadMap = prior ? Object.fromEntries((prior.lead_types || []).map((lt) => [lt.type, lt.count])) : {};

  const chatsDelta = prior ? computeDelta(rec.total_chats, prior.total_chats) : null;
  const leadsDelta = prior ? computeDelta(rec.total_leads, prior.total_leads) : null;
  const ratioDelta = prior ? computeDelta(rec.chat_to_lead_pct, prior.chat_to_lead_pct, 1) : null;

  const periodLabel = fmtPeriodLabel(selectedPeriod.start, selectedPeriod.end);
  const salesLeads = sortedLeadTypes.find((lt) => (lt.type || "").toLowerCase() === "sales")?.count || 0;

  return (
    <section
      className="bg-gradient-to-br from-slate-900 via-slate-900/95 to-cyan-900/15 rounded-2xl p-5 md:p-6 border border-cyan-400/20 mb-8"
      data-testid="live-chat-story-section"
    >
      {/* Header — mirrors ContentEffectCard collapse UX */}
      <div
        onClick={() => setIsOpen((v) => !v)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setIsOpen((v) => !v); } }}
        role="button"
        tabIndex={0}
        aria-expanded={isOpen}
        data-testid="live-chat-toggle"
        className="flex items-center gap-3 cursor-pointer select-none group"
      >
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center flex-shrink-0">
          <MessageCircle className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-cyan-300 text-[11px] font-bold uppercase tracking-[0.18em]">
            The Live Chat Story
          </p>
          <p className="text-white text-base md:text-lg font-bold leading-snug">
            {fmt(rec.total_chats)} real conversations on {clientName}&apos;s website.
          </p>
          <p className="text-slate-400 text-[11px] mt-0.5 tabular-nums">{periodLabel}</p>
          <p className="text-cyan-300/70 text-[11px] mt-0.5 group-hover:opacity-100 opacity-80 transition-opacity">
            {isOpen ? "Click to collapse" : "Click to expand"}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="px-3 py-1 bg-cyan-500/20 text-cyan-300 text-xs font-medium rounded-full border border-cyan-400/30">
            {rec.total_leads} lead{rec.total_leads !== 1 ? "s" : ""}
          </span>
          <ChevronDown className={`w-5 h-5 text-cyan-300 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
        </div>
      </div>

      {/* Body */}
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            key="live-chat-body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="pt-5">
              {/* Sub-headline + period switcher */}
              <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
                <p className="text-slate-400 text-sm">
                  How many people struck up a real conversation on {clientName}&apos;s website, and how many turned into leads.
                </p>
                {periods.length > 1 && (
                  <div className="flex items-center gap-2">
                    <label className="text-[10px] text-slate-400 uppercase tracking-wide font-bold">Period</label>
                    <select
                      value={selectedPeriod.key}
                      onChange={(e) => setSelectedKey(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      className="bg-slate-800 border border-cyan-400/30 text-white text-sm rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-cyan-400/70 cursor-pointer"
                      data-testid="live-chat-period-select"
                    >
                      {periods.map((p) => (
                        <option key={p.key} value={p.key}>{fmtPeriodLabel(p.start, p.end)}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {/* KPI strip */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <KPI label="Total Chats" value={fmt(rec.total_chats)} delta={chatsDelta} testId="lc-kpi-chats" />
                <KPI label="Leads Captured" value={fmt(rec.total_leads)} delta={leadsDelta} testId="lc-kpi-leads" />
                <KPI label="Billable (Sales)" value={fmt(rec.billable_leads)} testId="lc-kpi-billable" />
                <KPI
                  label="Chat → Lead"
                  value={`${(rec.chat_to_lead_pct || 0).toFixed(rec.chat_to_lead_pct >= 10 ? 0 : 2)}%`}
                  delta={ratioDelta}
                  testId="lc-kpi-ratio"
                />
              </div>

              {/* Narrative callout */}
              {rec.total_chats > 0 && (
                <div className="bg-cyan-500/8 border border-cyan-400/25 rounded-xl p-4 mb-6" data-testid="lc-narrative">
                  <p className="text-slate-200 text-sm leading-relaxed">
                    Your Live Chat had{" "}
                    <span className="text-white font-bold">{rec.total_chats} real {rec.total_chats === 1 ? "conversation" : "conversations"}</span>
                    {" "}this period.{" "}
                    {rec.total_leads > 0 ? (
                      <>
                        <span className="text-cyan-300 font-semibold">{rec.total_leads}</span>
                        {" "}turned into {rec.total_leads === 1 ? "a lead" : "leads"}
                        {salesLeads > 0 ? (
                          <>, and <span className="text-lime-300 font-semibold">{salesLeads}</span> of those {salesLeads === 1 ? "was" : "were"} classified as sales-ready.</>
                        ) : "."}
                      </>
                    ) : (
                      <span className="text-slate-300">Every conversation is a chance to build trust, even the ones that don&apos;t convert this month.</span>
                    )}
                  </p>
                </div>
              )}

              {/* Lead type breakdown */}
              {sortedLeadTypes.length > 0 && (
                <div className="mb-6">
                  <p className="text-[10px] uppercase tracking-[0.16em] text-slate-400 font-bold mb-3">Lead Type Breakdown</p>
                  <div>
                    {sortedLeadTypes.map((lt, i) => {
                      const colour = LEAD_COLOURS[i % LEAD_COLOURS.length];
                      return (
                        <LeadTypeRow
                          key={lt.type}
                          label={lt.type}
                          count={lt.count || 0}
                          total={totalLeadTypeCount || 1}
                          colour={colour.bar}
                          prior={priorLeadMap[lt.type]}
                        />
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Footnote — diplomatic tone */}
              <p
                className="mt-5 pt-4 border-t border-cyan-400/10 text-slate-400 text-[11px] italic leading-relaxed"
                data-testid="live-chat-footnote"
              >
                Live Chat sits directly on {clientName}&apos;s website. Each conversation is a real person
                who was curious enough to reach out. Sales-ready leads are the ones that most closely
                match a paying customer profile. Ratios shift month to month based on seasonality and
                the mix of ad campaigns driving traffic. Your ad rep can walk through the trends any time.
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
